import { useMemo, useCallback, useRef, useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { ChartAreaLoader } from "../../Loading";
import { SecondaryColor } from "../../../common";

export interface DumbbellDataItem {
  state: string;
  currentPeak: number;
  allTimePeak: number;
  currentPeakDate?: string;
  allTimePeakDate?: string;
}

interface DumbbellChartProps {
  data: DumbbellDataItem[];
  currentLabel?: string;
  peakLabel?: string;
  unit?: string;
  loading?: boolean;
}

const CURRENT_COLOR = "#22d3ee";
const CURRENT_STROKE = "#0891b2";
const PEAK_COLOR = "#f87171";
const PEAK_STROKE = "#dc2626";
const LINE_COLOR = "#cbd5e1";
const LABEL_CURRENT = "#0e7490";
const LABEL_PEAK = "#b91c1c";
const STATE_NAME_COLOR = "#34656d";

const DOT_R = 8;

function computeYBounds(items: { currentPeak: number; allTimePeak: number }[]) {
  const values = items
    .flatMap((d) => [d.currentPeak, d.allTimePeak])
    .filter((v) => v > 0);
  if (values.length === 0)
    return { min: 0, max: undefined as number | undefined };
  const dataMax = Math.max(...values);
  const yMax = Math.ceil((dataMax * 1.14) / 100) * 100;
  return { min: -(yMax * 0.04), max: yMax };
}

export function DumbbellChart({
  data,
  currentLabel = "Current Peak",
  peakLabel = "All Time Peak",
  unit = "MW",
  loading = false,
}: DumbbellChartProps) {
  const safeData = useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter(
        (d) => d.currentPeak > 0 || d.allTimePeak > 0,
      ),
    [data],
  );

  // Keep a ref so the dataZoom handler always sees the latest data without stale closure.
  const safeDataRef = useRef(safeData);
  useEffect(() => {
    safeDataRef.current = safeData;
  }, [safeData]);

  const chartRef = useRef<any>(null);

  // Dynamically compute chart height so the brush always sits at the visual bottom
  // on any screen size. Uses ResizeObserver so the measurement fires correctly even
  // when the component is initially mounted inside a display:none ancestor — plain
  // getBoundingClientRect() returns top=0 while hidden, which inflates chartHeight
  // and pushes the brush far below the viewport.
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(480);

  useEffect(() => {
    const FOOTER_MARGIN = 75;
    const LEGEND_HEIGHT = 38;

    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // width === 0 means the element (or an ancestor) is still display:none — skip
      if (rect.width === 0) return;
      const available = window.innerHeight - rect.top - FOOTER_MARGIN - LEGEND_HEIGHT;
      setChartHeight(Math.max(300, available));
    };

    // ResizeObserver fires when the element gains real dimensions (i.e. when the
    // parent's display:none is removed), not just on window resize.
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // On every slide/zoom, recompute y-axis bounds from the visible slice only.
  const handleDataZoom = useCallback((params: any) => {
    const zoom = params.batch ? params.batch[0] : params;
    const start: number = zoom?.start ?? 0;
    const end: number = zoom?.end ?? 100;
    const all = safeDataRef.current;
    const total = all.length;
    if (total === 0) return;
    const startIdx = Math.max(0, Math.floor((start / 100) * total));
    const endIdx = Math.min(total, Math.ceil((end / 100) * total));
    const visible = all.slice(startIdx, endIdx);
    if (visible.length === 0) return;
    const { min, max } = computeYBounds(visible);
    chartRef.current
      ?.getEchartsInstance?.()
      ?.setOption({ yAxis: { min, max } }, false);
  }, []);

  const option = useMemo(() => {
    const stateNames = safeData.map((d) => d.state);

    const fmtDate = (d?: string) => {
      if (!d) return "";
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return "";
      return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const tooltipHtml = (
      state: string,
      curVal: number,
      peakVal: number,
      curDate?: string,
      peakDate?: string,
    ) => {
      const gap = peakVal - curVal;
      const pct = peakVal > 0 ? ((gap / peakVal) * 100).toFixed(1) : "0.0";
      const fmt = (v: number) => v.toLocaleString("en-IN");
      const curDateLabel = fmtDate(curDate);
      const peakDateLabel = fmtDate(peakDate);

      const css = `<style>
        .db-tt{background:rgba(255,255,255,0.95);backdrop-filter:saturate(140%) blur(14px);-webkit-backdrop-filter:saturate(140%) blur(14px);box-shadow:0 1px 2px rgba(15,23,42,0.06),0 8px 24px -6px rgba(15,23,42,0.18),0 24px 48px -12px rgba(15,23,42,0.16);border:1px solid rgba(15,23,42,0.08);border-radius:14px;font-family:'Inter','SF Pro Text',system-ui,sans-serif;min-width:280px;padding:14px 14px 10px;color:#334155;letter-spacing:-0.005em}
        .db-header{padding:0 2px 12px;margin-bottom:8px;border-bottom:1px dashed rgba(15,23,42,0.09)}
        .db-kicker{display:inline-flex;align-items:center;gap:5px;font-size:9px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#64748b;margin-bottom:6px}
        .db-kicker-dot{width:5px;height:5px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);box-shadow:0 0 0 3px rgba(16,185,129,0.15)}
        .db-title{font-weight:650;font-size:17px;color:#334155;line-height:1.15;letter-spacing:-0.02em}
        .db-row{display:flex;align-items:center;gap:10px;padding:7px 8px 7px 10px;border-radius:8px}
        .db-row+.db-row{box-shadow:inset 0 1px 0 rgba(15,23,42,0.04)}
        .db-bar{flex-shrink:0;width:3px;height:18px;border-radius:2px}
        .db-name{flex:1;font-size:12px;font-weight:500;color:#64748b}
        .db-date{font-size:11px;font-weight:500;color:#94a3b8}
        .db-val{font-variant-numeric:tabular-nums;font-weight:650;font-size:14px;color:#334155;letter-spacing:-0.02em}
        .db-unit{font-size:11px;font-weight:600;color:#34656D;letter-spacing:0.05em;text-transform:uppercase;background:rgba(15,23,42,0.04);padding:1px 5px;border-radius:4px;margin-left:6px}
        .db-gap{display:flex;align-items:center;gap:10px;padding:8px 10px;margin-top:6px;border-top:1px dashed rgba(15,23,42,0.09);font-size:11px;color:#64748b}
        .db-gap b{color:#f59e0b;font-variant-numeric:tabular-nums}
      </style>`;

      return `${css}<div class="db-tt">
        <div class="db-header">
          <div class="db-kicker"><span class="db-kicker-dot"></span>Snapshot</div>
          <div class="db-title">${state}</div>
        </div>
        <div class="db-row">
          <span class="db-bar" style="background:linear-gradient(180deg,${CURRENT_COLOR},${CURRENT_STROKE})"></span>
          <span class="db-name">${currentLabel}${curDateLabel ? ` <span class="db-date">(${curDateLabel})</span>` : ""}</span>
          <span class="db-val">${fmt(curVal)}</span><span class="db-unit">${unit}</span>
        </div>
        <div class="db-row">
          <span class="db-bar" style="background:linear-gradient(180deg,${PEAK_COLOR},${PEAK_STROKE})"></span>
          <span class="db-name">${peakLabel}${peakDateLabel ? ` <span class="db-date">(${peakDateLabel})</span>` : ""}</span>
          <span class="db-val">${fmt(peakVal)}</span><span class="db-unit">${unit}</span>
        </div>
        <div class="db-gap">
          Gap:&nbsp;<b>${fmt(gap)} ${unit}</b>&nbsp;·&nbsp;${pct}% below peak
        </div>
      </div>`;
    };

    // Initial bounds from full dataset; dynamically overridden on slide via handleDataZoom.
    const { min: yAxisMin, max: yAxisMax } = computeYBounds(safeData);

    // Show ~12 states initially; user slides to see the rest
    const initialEnd =
      safeData.length > 12 ? Math.round((12 / safeData.length) * 100) : 100;
    return {
      animation: true,
      backgroundColor: "transparent",

      tooltip: {
        trigger: "item",
        backgroundColor: "transparent",
        borderColor: "transparent",
        borderWidth: 0,
        padding: 0,
        extraCssText: "box-shadow:none;",
        formatter: (params: any) => {
          const v = params?.value ?? ["", 0, 0];
          return tooltipHtml(
            String(v[0]),
            Number(v[1]),
            Number(v[2]),
            v[3] as string | undefined,
            v[4] as string | undefined,
          );
        },
      },

      grid: {
        left: 55,
        right: 20,
        top: 50,
        bottom: 145,
        containLabel: false,
      },
      // ── X-axis: state names ────────────────────────────────────────────────
      xAxis: {
        type: "category",
        data: stateNames.map((name) => ({
          value: name,
          textStyle: {
            color: STATE_NAME_COLOR,
            fontSize: 10,
            fontWeight: "normal" as const,
          },
        })),
        axisLabel: {
          interval: 0,
          color: STATE_NAME_COLOR,
          fontSize: 10,
          rotate: 40,
          align: "right" as const,
          margin: 18,
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#e2e8f0" } },
        splitLine: { show: false },
      },

      // ── Y-axis: numeric values ─────────────────────────────────────────────
      yAxis: {
        type: "value",
        min: yAxisMin,
        max: yAxisMax,
        name: unit,
        nameLocation: "middle" as const,
        nameGap: 40,
        nameRotate: 90,
        nameTextStyle: {
          color: SecondaryColor,
          fontSize: 10,
          fontWeight: "600" as const,
        },
        axisLabel: {
          formatter: (v: number) =>
            v < 0 ? "" : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v),
          fontSize: 10,
          color: "#94a3b8",
        },
        axisLine: { lineStyle: { color: "#e2e8f0" } },
        splitLine: {
          lineStyle: { type: "solid" as const, color: "#f1f5f9", width: 1 },
        },
      },

      // ── Brush / slide ──────────────────────────────────────────────────────
      dataZoom: [
        {
          type: "slider",
          xAxisIndex: [0],
          start: 0,
          end: initialEnd,
          height: 17,
          width: "98%",
          bottom: 5,
          left: 10,
          brushSelect: false,
          textStyle: { color: SecondaryColor },
          handleIcon:
            "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
          handleSize: "80%",
          filterMode: "filter" as const,
        },
        {
          type: "inside",
          xAxisIndex: 0,
          start: 0,
          end: initialEnd,
          filterMode: "filter" as const,
        },
      ],

      series: [
        {
          type: "custom",
          name: "dumbbell",
          clip: false,
          encode: { x: 0, y: [1, 2] },
          emphasis: { disabled: true },
          renderItem: (_params: any, api: any) => {
            const stateName = api.value(0);
            const current: number = api.value(1);
            const peak: number = api.value(2);

            // x = horizontal center of this state's column
            // cy / py = vertical pixel positions for current and peak
            const [x, cy] = api.coord([stateName, current]);
            const [, py] = api.coord([stateName, peak]);

            const topY = Math.min(cy, py); // smaller y = higher on screen
            const botY = Math.max(cy, py);

            const children: any[] = [
              // ── vertical connecting line ─────────────────────────
              {
                type: "line",
                shape: { x1: x, y1: topY, x2: x, y2: botY },
                style: {
                  stroke: LINE_COLOR,
                  lineWidth: 3,
                  lineCap: "round" as const,
                },
                z: 1,
              },
              // ── cyan dot (current) ───────────────────────────────
              {
                type: "circle",
                shape: { cx: x, cy, r: DOT_R },
                style: {
                  fill: CURRENT_COLOR,
                  stroke: CURRENT_STROKE,
                  lineWidth: 2,
                  shadowBlur: 5,
                  shadowColor: "rgba(8,145,178,0.35)",
                },
                z: 2,
              },
              // ── red dot (all-time peak) ──────────────────────────
              {
                type: "circle",
                shape: { cx: x, cy: py, r: DOT_R },
                style: {
                  fill: PEAK_COLOR,
                  stroke: PEAK_STROKE,
                  lineWidth: 2,
                  shadowBlur: 5,
                  shadowColor: "rgba(220,38,38,0.35)",
                },
                z: 2,
              },
            ];

            const fmt = (v: number) =>
              v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}M`
              : v >= 1000    ? `${(v / 1000).toFixed(0)}K`
              : `${Math.round(v)}`;

            const LABEL_OFFSET  = 13;
            const GRID_TOP_PX   = 20;
            const GRID_BOT_PX   = api.getHeight() - 40;

            const topVal  = cy < py ? current : peak;
            const botVal  = cy < py ? peak    : current;
            const topClr  = cy < py ? LABEL_CURRENT : LABEL_PEAK;
            const botClr  = cy < py ? LABEL_PEAK    : LABEL_CURRENT;

            children.push({
              type: "text",
              style: {
                text: fmt(topVal),
                x, y: Math.max(topY - LABEL_OFFSET, GRID_TOP_PX),
                textAlign: "center" as const,
                textVerticalAlign: "bottom" as const,
                fill: topClr, fontSize: 11, fontWeight: "bold" as const,
              },
              z: 3,
            });

            children.push({
              type: "text",
              style: {
                text: fmt(botVal),
                x, y: Math.min(botY + LABEL_OFFSET, GRID_BOT_PX),
                textAlign: "center" as const,
                textVerticalAlign: "top" as const,
                fill: botClr, fontSize: 11, fontWeight: "bold" as const,
              },
              z: 3,
            });

            return { type: "group", children };
          },
          data: safeData.map((d) => [
            d.state,
            d.currentPeak,
            d.allTimePeak,
            d.currentPeakDate,
            d.allTimePeakDate,
          ]),
        },
      ],
    };
  }, [safeData, currentLabel, peakLabel, unit]);

  if (loading) return <ChartAreaLoader />;

  if (safeData.length === 0) {
    return (
      <div
        style={{ height: 220 }}
        className="flex flex-col items-center justify-center gap-2 text-gray-400"
      >
        <svg width="44" height="14" viewBox="0 0 44 14" fill="none">
          <circle
            cx="7"
            cy="7"
            r="6"
            fill={CURRENT_COLOR}
            stroke={CURRENT_STROKE}
            strokeWidth="1.5"
          />
          <line
            x1="13"
            y1="7"
            x2="31"
            y2="7"
            stroke={LINE_COLOR}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <circle
            cx="37"
            cy="7"
            r="6"
            fill={PEAK_COLOR}
            stroke={PEAK_STROKE}
            strokeWidth="1.5"
          />
        </svg>
        <span className="text-sm font-medium">
          No data for selected filters
        </span>
        <span className="text-xs text-gray-300">
          Try selecting a longer date range
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      {/* Legend */}
      <div className="flex gap-6 items-center justify-end px-4 pb-2 text-xs font-semibold select-none">
        <span
          className="flex items-center gap-2"
          style={{ color: LABEL_CURRENT }}
        >
          <span
            style={{
              display: "inline-block",
              width: 13,
              height: 13,
              borderRadius: "50%",
              background: CURRENT_COLOR,
              border: `2px solid ${CURRENT_STROKE}`,
              boxShadow: "0 1px 4px rgba(8,145,178,0.4)",
            }}
          />
          {currentLabel}
        </span>
        <span className="flex items-center gap-2" style={{ color: LABEL_PEAK }}>
          <span
            style={{
              display: "inline-block",
              width: 13,
              height: 13,
              borderRadius: "50%",
              background: PEAK_COLOR,
              border: `2px solid ${PEAK_STROKE}`,
              boxShadow: "0 1px 4px rgba(220,38,38,0.4)",
            }}
          />
          {peakLabel}
        </span>
      </div>

      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ width: "100%", height: chartHeight }}
        notMerge={true}
        lazyUpdate={false}
        onEvents={{ dataZoom: handleDataZoom }}
      />
    </div>
  );
}
